import React, { useEffect, useCallback } from "react";
import styled from "styled-components";
import {
    FocusContext,
    useFocusable,
    setFocus,
    init,
} from "@noriginmedia/norigin-spatial-navigation";
import { forceAppUpdate } from "../../utils/pwaManager";
import { useInputMode } from "../../utils/hooks/useInputMode";

/**
 * Force Update Modal
 * 
 * Un-dismissable modal that appears when app version is below minimum required.
 * Focus is trapped on the Update button - no escape, no backdrop dismiss.
 * Uses legacy CSS for Smart TV compatibility (LG WebOS / Samsung Tizen).
 */

// Ensure spatial navigation is initialized
init({
    debug: false,
    visualDebug: false,
});

// Full-screen overlay
var Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -webkit-align-items: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  -ms-flex-pack: center;
  justify-content: center;
  z-index: 99999;
  direction: rtl;
`;

// Modal card
var Card = styled.div`
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.98) 0%, rgba(20, 20, 20, 0.98) 100%);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 24px;
  padding: 60px 80px;
  text-align: center;
  max-width: 600px;
  -webkit-box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
`;

// Update icon
var IconWrapper = styled.div`
  width: 100px;
  height: 100px;
  margin: 0 auto 32px;
  background: linear-gradient(135deg, rgba(133, 0, 0, 0.3) 0%, rgba(133, 0, 0, 0.15) 100%);
  border: 2px solid rgba(133, 0, 0, 0.5);
  border-radius: 50%;
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -webkit-align-items: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  -ms-flex-pack: center;
  justify-content: center;
  font-size: 48px;
`;

var Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #ffffff;
  font-family: "AnjomanMax", sans-serif;
  margin: 0 0 16px;
`;

var Description = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.7);
  font-family: "AnjomanMax", sans-serif;
  margin: 0 0 40px;
  line-height: 1.6;
`;

var UpdateButtonStyled = styled.button`
  width: 100%;
  max-width: 320px;
  height: 64px;
  background: ${function (props) {
        return props.focused
            ? "linear-gradient(135deg, #a50000 0%, #850000 100%)"
            : "linear-gradient(135deg, #850000 0%, #6a0000 100%)";
    }};
  border: ${function (props) {
        return props.focused
            ? "3px solid #ffffff"
            : "2px solid rgba(255, 255, 255, 0.2)";
    }};
  border-radius: 16px;
  color: #ffffff;
  font-size: 22px;
  font-weight: 700;
  font-family: "AnjomanMax", sans-serif;
  cursor: pointer;
  -webkit-transition: all 0.2s ease;
  -o-transition: all 0.2s ease;
  transition: all 0.2s ease;
  -webkit-transform: ${function (props) { return props.focused ? "scale(1.05)" : "scale(1)"; }};
  -ms-transform: ${function (props) { return props.focused ? "scale(1.05)" : "scale(1)"; }};
  transform: ${function (props) { return props.focused ? "scale(1.05)" : "scale(1)"; }};
  -webkit-box-shadow: ${function (props) {
        return props.focused
            ? "0 8px 32px rgba(133, 0, 0, 0.6)"
            : "0 4px 16px rgba(0, 0, 0, 0.3)";
    }};
  box-shadow: ${function (props) {
        return props.focused
            ? "0 8px 32px rgba(133, 0, 0, 0.6)"
            : "0 4px 16px rgba(0, 0, 0, 0.3)";
    }};
  outline: none;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

var LoadingText = styled.span`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.5);
  font-family: "AnjomanMax", sans-serif;
  margin-top: 24px;
  display: block;
`;

// Update button with focus handling
function UpdateButton({ onUpdate, isUpdating }) {
    var { ref, focused, focusSelf } = useFocusable({
        focusKey: "force-update-button",
        onEnterPress: function () {
            if (!isUpdating) {
                onUpdate();
            }
        },
    });

    var { setMouseFocus } = useInputMode();
    var handleMouseEnter = useCallback(function () {
        setMouseFocus(true);
        focusSelf();
    }, [focusSelf, setMouseFocus]);

    // Auto-focus on mount
    useEffect(function () {
        var timer = setTimeout(function () {
            focusSelf();
        }, 100);
        return function () { clearTimeout(timer); };
    }, [focusSelf]);

    // Keep focus trapped - re-focus if somehow lost
    useEffect(function () {
        if (!focused) {
            var timer = setTimeout(function () {
                setFocus("force-update-button");
            }, 200);
            return function () { clearTimeout(timer); };
        }
    }, [focused]);

    return (
        <UpdateButtonStyled
            ref={ref}
            onClick={function () {
                if (!isUpdating) {
                    onUpdate();
                }
            }}
            onMouseEnter={handleMouseEnter}
            focused={focused}
            disabled={isUpdating}
        >
            {isUpdating ? "در حال بروزرسانی..." : "بروزرسانی"}
        </UpdateButtonStyled>
    );
}

export default function ForceUpdateModal() {
    var { ref, focusKey } = useFocusable({
        focusKey: "force-update-modal",
        isFocusBoundary: true, // Trap focus within modal
        focusBoundaryDirections: ["up", "down", "left", "right"],
    });

    var [isUpdating, setIsUpdating] = React.useState(false);

    // Block back button and escape
    useEffect(function () {
        function handleKeyDown(e) {
            // Block Escape, Back, and browser back navigation
            if (
                e.keyCode === 27 || // Escape
                e.keyCode === 8 ||  // Backspace (browser back)
                e.keyCode === 461 || // LG WebOS Back
                e.keyCode === 10009 || // Samsung Tizen Back
                e.key === "GoBack" ||
                e.key === "BrowserBack"
            ) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
        }

        // Block popstate (browser back)
        function handlePopState(e) {
            e.preventDefault();
            window.history.pushState(null, "", window.location.href);
        }

        window.addEventListener("keydown", handleKeyDown, true);
        window.addEventListener("popstate", handlePopState);
        window.history.pushState(null, "", window.location.href);

        return function () {
            window.removeEventListener("keydown", handleKeyDown, true);
            window.removeEventListener("popstate", handlePopState);
        };
    }, []);

    function handleUpdate() {
        setIsUpdating(true);
        console.log("[ForceUpdateModal] Starting update...");

        // Force app update: clear cache, unregister SW, reload
        forceAppUpdate().catch(function (err) {
            console.error("[ForceUpdateModal] Update failed:", err);
            // Even if it fails, still reload
            window.location.reload(true);
        });
    }

    return (
        <FocusContext.Provider value={focusKey}>
            <Overlay ref={ref}>
                <Card>
                    <IconWrapper>🔄</IconWrapper>
                    <Title>نسخه جدید موجود است</Title>
                    <Description>
                        نسخه فعلی برنامه قدیمی است و برای ادامه استفاده باید بروزرسانی کنید.
                    </Description>
                    <UpdateButton onUpdate={handleUpdate} isUpdating={isUpdating} />
                    {isUpdating && (
                        <LoadingText>لطفاً صبر کنید...</LoadingText>
                    )}
                </Card>
            </Overlay>
        </FocusContext.Provider>
    );
}
